import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { RotateCcw, Hand } from 'lucide-react';
import { hapticTick, hapticSuccess } from '../../lib/haptics';

const STORAGE_KEY = 'tasbih_count';
const TARGET_KEY = 'tasbih_target';
const DHIKR_KEY = 'tasbih_dhikr';

// Adhkar classiques (récités notamment après la prière).
const DHIKRS = [
    { ar: 'سُبْحَانَ اللَّه', tr: 'SubhânâLlah', fr: 'Gloire à Allah' },
    { ar: 'الْحَمْدُ لِلَّه', tr: 'Alhamdou liLlah', fr: 'Louange à Allah' },
    { ar: 'اللَّهُ أَكْبَر', tr: 'Allâhou Akbar', fr: 'Allah est le plus grand' },
    { ar: 'لَا إِلَٰهَ إِلَّا اللَّه', tr: 'Lâ ilâha illâ Llah', fr: 'Nulle divinité sauf Allah' },
];

const TARGETS = [33, 99, 100];

const readInt = (key, fallback) => {
    const parsed = parseInt(localStorage.getItem(key) ?? '', 10);
    return Number.isNaN(parsed) ? fallback : parsed;
};

export const Tasbih = () => {
    const [count, setCount] = useState(() => readInt(STORAGE_KEY, 0));
    const [target, setTarget] = useState(() => {
        const t = readInt(TARGET_KEY, 33);
        return TARGETS.includes(t) ? t : 33;
    });
    const [dhikrIdx, setDhikrIdx] = useState(() => {
        const i = readInt(DHIKR_KEY, 0);
        return i >= 0 && i < DHIKRS.length ? i : 0;
    });

    // Persistance.
    useEffect(() => { localStorage.setItem(STORAGE_KEY, String(count)); }, [count]);
    useEffect(() => { localStorage.setItem(TARGET_KEY, String(target)); }, [target]);
    useEffect(() => { localStorage.setItem(DHIKR_KEY, String(dhikrIdx)); }, [dhikrIdx]);

    // Incrémente + retour haptique natif (vibration forte à la complétion
    // d'un cycle, légère sinon). hapticTick/hapticSuccess gèrent eux-mêmes le
    // repli sur navigator.vibrate sur le web.
    const increment = useCallback(() => {
        setCount((prev) => {
            const next = prev + 1;
            if (next % target === 0) hapticSuccess();
            else hapticTick();
            return next;
        });
    }, [target]);

    const reset = useCallback(() => {
        setCount(0);
    }, []);

    // Comptage au clavier (Espace) sur ordinateur, sauf si l'on saisit du texte.
    useEffect(() => {
        const onKey = (e) => {
            if (e.code !== 'Space' && e.key !== ' ') return;
            const tag = (e.target?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
            e.preventDefault();
            increment();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [increment]);

    const inCycle = count % target;
    const cyclesCompleted = Math.floor(count / target);
    const dhikr = DHIKRS[dhikrIdx];

    // Anneau de progression (SVG).
    const R = 92;
    const C = 2 * Math.PI * R;
    const progress = inCycle / target; // 0..1 (0 quand on vient de boucler)
    const dashOffset = C * (1 - progress);

    return (
        <Card className="rounded-3xl border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 h-full">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-emerald-700 dark:text-emerald-300">
                    <Hand className="h-5 w-5" />
                    Tasbih (Compteur de dhikr)
                </CardTitle>
            </CardHeader>

            <CardContent className="flex flex-col items-center gap-4 pb-6">
                {/* Sélecteur de dhikr */}
                <div className="flex flex-wrap justify-center gap-1.5">
                    {DHIKRS.map((d, i) => (
                        <button
                            key={d.tr}
                            type="button"
                            onClick={() => setDhikrIdx(i)}
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                i === dhikrIdx
                                    ? 'border-emerald-400 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                                    : 'border-border/60 text-muted-foreground hover:bg-muted'
                            }`}
                        >
                            {d.tr}
                        </button>
                    ))}
                </div>

                {/* Dhikr sélectionné */}
                <div className="text-center">
                    <p className="text-2xl leading-tight text-emerald-800 dark:text-emerald-200" dir="rtl" lang="ar">{dhikr.ar}</p>
                    <p className="text-xs text-muted-foreground">{dhikr.fr}</p>
                </div>

                {/* Indicateurs */}
                <div className="flex w-full items-center justify-around text-center">
                    <div>
                        <p className="text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{inCycle}</p>
                        <p className="text-xs text-muted-foreground">/ {target} en cours</p>
                    </div>
                    <div className="h-10 w-px bg-emerald-200 dark:bg-emerald-800" />
                    <div>
                        <p className="text-2xl font-bold tabular-nums text-teal-700 dark:text-teal-300">{cyclesCompleted}</p>
                        <p className="text-xs text-muted-foreground">cycles</p>
                    </div>
                    <div className="h-10 w-px bg-emerald-200 dark:bg-emerald-800" />
                    <div>
                        <p className="text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{count}</p>
                        <p className="text-xs text-muted-foreground">total</p>
                    </div>
                </div>

                {/* Bouton central avec anneau de progression */}
                <button
                    type="button"
                    onClick={increment}
                    aria-label="Incrémenter le dhikr"
                    className="group relative flex h-48 w-48 select-none items-center justify-center rounded-full transition-transform active:scale-95 focus:outline-none"
                >
                    <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full -rotate-90">
                        <circle cx="100" cy="100" r={R} fill="none" strokeWidth="10" className="stroke-emerald-200/70 dark:stroke-emerald-900/60" />
                        <circle
                            cx="100" cy="100" r={R} fill="none" strokeWidth="10" strokeLinecap="round"
                            className="stroke-emerald-500 transition-[stroke-dashoffset] duration-200"
                            strokeDasharray={C}
                            strokeDashoffset={dashOffset}
                        />
                    </svg>
                    <span className="flex h-36 w-36 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xl ring-4 ring-white/40 group-focus-visible:ring-emerald-300 dark:ring-emerald-950/40">
                        <span className="text-5xl font-bold tabular-nums">{inCycle}</span>
                    </span>
                </button>

                <p className="text-center text-xs text-muted-foreground">
                    Touchez le cercle (ou appuyez sur <kbd className="rounded border px-1">Espace</kbd>) pour compter.
                </p>

                {/* Cible + réinitialisation */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 rounded-full border border-emerald-200 p-0.5 dark:border-emerald-800">
                        {TARGETS.map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTarget(t)}
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                                    target === t ? 'bg-emerald-500 text-white' : 'text-emerald-700 dark:text-emerald-300'
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                    <Button
                        onClick={reset}
                        variant="outline"
                        size="sm"
                        className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:text-emerald-300"
                        disabled={count === 0}
                    >
                        <RotateCcw className="h-4 w-4" />
                        Réinitialiser
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};
